// This file is auto-generated by @hey-api/openapi-ts

import type { Client, Options as Options2, TDataShape } from "./client";
import { client } from "./client.gen";
import type {
  AdminActivityActionsData,
  AdminActivityActionsResponses,
  AdminActivityAnalyticsData,
  AdminActivityAnalyticsErrors,
  AdminActivityAnalyticsResponses,
  AdminDeleteUserData,
  AdminDeleteUserErrors,
  AdminDeleteUserResponses,
  AdminGetUserData,
  AdminGetUserErrors,
  AdminGetUserResponses,
  AdminListActivityData,
  AdminListActivityErrors,
  AdminListActivityResponses,
  AdminListUsersData,
  AdminListUsersResponses,
  AdminUpdateUserData,
  AdminUpdateUserErrors,
  AdminUpdateUserResponses,
  ApproveNudgeData,
  ApproveNudgeErrors,
  ApproveNudgeResponses,
  DemoNextEventData,
  DemoNextEventResponses,
  DemoResetData,
  DemoResetResponses,
  GetActivityData,
  GetActivityResponses,
  GetAuthConfigData,
  GetAuthConfigResponses,
  GetCustomerPreferencesData,
  GetCustomerPreferencesErrors,
  GetCustomerPreferencesResponses,
  GetDemoQueueData,
  GetDemoQueueResponses,
  GetHealthData,
  GetHealthResponses,
  GetMeData,
  GetMeDetailData,
  GetMeDetailResponses,
  GetMeResponses,
  GetMyVoteData,
  GetMyVoteResponses,
  GetOrderingCustomerData,
  GetOrderingCustomerErrors,
  GetOrderingCustomerResponses,
  GetShareAccountData,
  GetShareAccountErrors,
  GetShareAccountResponses,
  GetShareDraftData,
  GetShareDraftErrors,
  GetShareDraftResponses,
  GetShareQuartersData,
  GetShareQuartersResponses,
  GetShareSummaryData,
  GetShareSummaryErrors,
  GetShareSummaryResponses,
  GetVoteSummaryData,
  GetVoteSummaryResponses,
  GetVoteThemesData,
  GetVoteThemesResponses,
  ListOrderEmailsData,
  ListOrderEmailsErrors,
  ListOrderEmailsResponses,
  ListOrderingCustomersData,
  ListOrderingCustomersErrors,
  ListOrderingCustomersResponses,
  ListShareNudgesData,
  ListShareNudgesErrors,
  ListShareNudgesResponses,
  LoginWithPasswordData,
  LoginWithPasswordErrors,
  LoginWithPasswordResponses,
  LogoutData,
  LogoutResponses,
  ResetVotesData,
  ResetVotesResponses,
  SkipNudgeData,
  SkipNudgeErrors,
  SkipNudgeResponses,
  SsoCallbackData,
  SsoLoginData,
  SubmitVoteData,
  SubmitVoteErrors,
  SubmitVoteResponses,
  UpdateCustomerPreferencesData,
  UpdateCustomerPreferencesErrors,
  UpdateCustomerPreferencesResponses,
  UpdateProfileData,
  UpdateProfileErrors,
  UpdateProfileResponses,
} from "./types.gen";

export type Options<
  TData extends TDataShape = TDataShape,
  ThrowOnError extends boolean = boolean,
  TResponse = unknown,
> = Options2<TData, ThrowOnError, TResponse> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client;
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: Record<string, unknown>;
};

/**
 * Reset Votes
 *
 * Delete every vote and clear the clustering state — a between-demo reset.
 *
 * Protected: only an authenticated presenter can wipe the ballot.
 */
export const resetVotes = <ThrowOnError extends boolean = false>(
  options?: Options<ResetVotesData, ThrowOnError>,
) =>
  (options?.client ?? client).delete<ResetVotesResponses, unknown, ThrowOnError>({
    url: "/votes",
    ...options,
  });

/**
 * Submit Vote
 *
 * Record (or update) the caller's vote, keyed on the `boi-vote-id` cookie.
 *
 * A first-time voter has no valid cookie, so one is generated and set on the
 * response; a malformed cookie is treated as first-time (never fed to the uuid
 * primary key, which would 500). Re-voting from the same browser reuses the
 * cookie and upserts the existing row rather than adding a duplicate. Once the
 * voter cap is reached, only existing voters may keep updating — a new voter
 * is rejected with 429.
 */
export const submitVote = <ThrowOnError extends boolean = false>(
  options: Options<SubmitVoteData, ThrowOnError>,
) =>
  (options.client ?? client).post<SubmitVoteResponses, SubmitVoteErrors, ThrowOnError>({
    url: "/votes",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Get My Vote
 *
 * Return the caller's own vote, resolved from their `boi-vote-id` cookie.
 *
 * No cookie, a malformed cookie, or no stored row → `voted=false` with a null
 * vote; otherwise the stored score and comments come back so the /vote page can
 * prefill an edit.
 */
export const getMyVote = <ThrowOnError extends boolean = false>(
  options?: Options<GetMyVoteData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetMyVoteResponses, unknown, ThrowOnError>({
    url: "/votes/me",
    ...options,
  });

/**
 * Get Vote Summary
 *
 * Live vote stats for the presenter: total, average, and a 1-5 distribution.
 *
 * Cheap enough for the frontend to poll every few seconds — one table read and
 * a Python aggregation, no LLM. `distribution` is always five zero-filled
 * buckets (scores 1..5); `average` is null when no votes exist.
 */
export const getVoteSummary = <ThrowOnError extends boolean = false>(
  options?: Options<GetVoteSummaryData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetVoteSummaryResponses, unknown, ThrowOnError>({
    url: "/votes/summary",
    ...options,
  });

/**
 * Get Vote Themes
 *
 * Cluster audience pros and cons into labeled themes for the presenter.
 *
 * The presenter auto-polls this while votes stream in. Pros and cons are
 * clustered concurrently and independently, each biggest-theme-first. Existing
 * themes are frozen across polls and only *appended* to (a full rebuild happens
 * only when a re-vote removes a previously-clustered comment), so labels and
 * groupings stay stable. Counts and verbatim examples are always derived
 * deterministically — the model never authors a count or a quote — and a
 * clustering failure degrades rather than 500s.
 */
export const getVoteThemes = <ThrowOnError extends boolean = false>(
  options?: Options<GetVoteThemesData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetVoteThemesResponses, unknown, ThrowOnError>({
    url: "/votes/themes",
    ...options,
  });

/**
 * List Ordering Customers
 *
 * One page of ordering customers with their status dot, counts, and opt-in.
 *
 * `q` filters by a case-insensitive substring of the account name.
 * `attention` filters to one status dot ("ok"/"progress"/"risk"), evaluated
 * over the full `q`-filtered population before pagination so it composes
 * correctly with `limit`/`offset`/`total`.
 */
export const listOrderingCustomers = <ThrowOnError extends boolean = false>(
  options?: Options<ListOrderingCustomersData, ThrowOnError>,
) =>
  (options?.client ?? client).get<
    ListOrderingCustomersResponses,
    ListOrderingCustomersErrors,
    ThrowOnError
  >({ url: "/comms/ordering/customers", ...options });

/**
 * Get Ordering Customer
 *
 * One customer's detail: summary plus its orders and stepper state.
 */
export const getOrderingCustomer = <ThrowOnError extends boolean = false>(
  options: Options<GetOrderingCustomerData, ThrowOnError>,
) =>
  (options.client ?? client).get<
    GetOrderingCustomerResponses,
    GetOrderingCustomerErrors,
    ThrowOnError
  >({ url: "/comms/ordering/customers/{account_id}", ...options });

/**
 * List Order Emails
 *
 * One order's email/suppression log, oldest first.
 */
export const listOrderEmails = <ThrowOnError extends boolean = false>(
  options: Options<ListOrderEmailsData, ThrowOnError>,
) =>
  (options.client ?? client).get<ListOrderEmailsResponses, ListOrderEmailsErrors, ThrowOnError>({
    url: "/comms/ordering/orders/{order_id}/emails",
    ...options,
  });

/**
 * Get Customer Preferences
 *
 * One customer's per-event-type notification toggles (7 keys).
 */
export const getCustomerPreferences = <ThrowOnError extends boolean = false>(
  options: Options<GetCustomerPreferencesData, ThrowOnError>,
) =>
  (options.client ?? client).get<
    GetCustomerPreferencesResponses,
    GetCustomerPreferencesErrors,
    ThrowOnError
  >({ url: "/comms/ordering/customers/{account_id}/preferences", ...options });

/**
 * Update Customer Preferences
 *
 * Replace one customer's 7 preference toggles; returns the same shape back.
 */
export const updateCustomerPreferences = <ThrowOnError extends boolean = false>(
  options: Options<UpdateCustomerPreferencesData, ThrowOnError>,
) =>
  (options.client ?? client).put<
    UpdateCustomerPreferencesResponses,
    UpdateCustomerPreferencesErrors,
    ThrowOnError
  >({
    url: "/comms/ordering/customers/{account_id}/preferences",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Get Share Quarters
 *
 * Every quarter the share programme has data for, plus the live one.
 */
export const getShareQuarters = <ThrowOnError extends boolean = false>(
  options?: Options<GetShareQuartersData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetShareQuartersResponses, unknown, ThrowOnError>({
    url: "/comms/share/quarters",
    ...options,
  });

/**
 * Get Share Summary
 *
 * The share list screen's rows + KPIs for one quarter (live/closed/future).
 *
 * KPIs are always org-wide; `rows` is one page, filtered by `q` (a
 * case-insensitive substring of the account name) if given.
 */
export const getShareSummary = <ThrowOnError extends boolean = false>(
  options: Options<GetShareSummaryData, ThrowOnError>,
) =>
  (options.client ?? client).get<GetShareSummaryResponses, GetShareSummaryErrors, ThrowOnError>({
    url: "/comms/share/summary",
    ...options,
  });

/**
 * Get Share Account
 *
 * One preferred account's detail page: KPIs, trend series, mix, pending flag.
 */
export const getShareAccount = <ThrowOnError extends boolean = false>(
  options: Options<GetShareAccountData, ThrowOnError>,
) =>
  (options.client ?? client).get<GetShareAccountResponses, GetShareAccountErrors, ThrowOnError>({
    url: "/comms/share/accounts/{account_id}",
    ...options,
  });

/**
 * List Share Nudges
 *
 * Delivered share-nudge emails for one preferred account, newest first.
 */
export const listShareNudges = <ThrowOnError extends boolean = false>(
  options: Options<ListShareNudgesData, ThrowOnError>,
) =>
  (options.client ?? client).get<ListShareNudgesResponses, ListShareNudgesErrors, ThrowOnError>({
    url: "/comms/share/accounts/{account_id}/nudges",
    ...options,
  });

/**
 * Get Share Draft
 *
 * The current share-nudge draft for one account's live quarter.
 */
export const getShareDraft = <ThrowOnError extends boolean = false>(
  options: Options<GetShareDraftData, ThrowOnError>,
) =>
  (options.client ?? client).get<GetShareDraftResponses, GetShareDraftErrors, ThrowOnError>({
    url: "/comms/share/accounts/{account_id}/draft",
    ...options,
  });

/**
 * Approve Nudge
 *
 * Approve a pending share-nudge draft — writes the email record and sends it.
 */
export const approveNudge = <ThrowOnError extends boolean = false>(
  options: Options<ApproveNudgeData, ThrowOnError>,
) =>
  (options.client ?? client).post<ApproveNudgeResponses, ApproveNudgeErrors, ThrowOnError>({
    url: "/comms/share/accounts/{account_id}/draft/approve",
    ...options,
  });

/**
 * Skip Nudge
 *
 * Decline a pending share-nudge draft — no email record is written.
 */
export const skipNudge = <ThrowOnError extends boolean = false>(
  options: Options<SkipNudgeData, ThrowOnError>,
) =>
  (options.client ?? client).post<SkipNudgeResponses, SkipNudgeErrors, ThrowOnError>({
    url: "/comms/share/accounts/{account_id}/draft/skip",
    ...options,
  });

/**
 * Demo Reset
 *
 * Restore the demo to its pristine state: unprocessed queue, no nudge actions.
 *
 * Undoes only what the demo itself created, leaving the seeded history
 * intact: deletes the demo-created email records (share-nudge approvals +
 * order-event emails for `demo_queued` events), re-opens the `demo_queued`
 * events (restoring the demo-trigger queue), and reverts every approval-kind
 * nudge to pending. Auto-kind nudges are untouched — they're always
 * `state=sent` by construction (`nudges_kind_state_check`).
 *
 * Deliberately scoped to the demo artifacts rather than the whole tables: a
 * full-table wipe/rewrite over the real load (~312k rows each) exceeds
 * Postgres' `statement_timeout`, which is why the reset appeared to do
 * nothing.
 */
export const demoReset = <ThrowOnError extends boolean = false>(
  options?: Options<DemoResetData, ThrowOnError>,
) =>
  (options?.client ?? client).post<DemoResetResponses, unknown, ThrowOnError>({
    url: "/admin/demo/reset",
    ...options,
  });

/**
 * Demo Next Event
 *
 * Pop and process the oldest queued order event; 409 when the queue is empty.
 */
export const demoNextEvent = <ThrowOnError extends boolean = false>(
  options?: Options<DemoNextEventData, ThrowOnError>,
) =>
  (options?.client ?? client).post<DemoNextEventResponses, unknown, ThrowOnError>({
    url: "/admin/demo/next-event",
    ...options,
  });

/**
 * Get Demo Queue
 *
 * How many order events remain queued, plus a peek at the next one.
 */
export const getDemoQueue = <ThrowOnError extends boolean = false>(
  options?: Options<GetDemoQueueData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetDemoQueueResponses, unknown, ThrowOnError>({
    url: "/admin/demo/queue",
    ...options,
  });

/**
 * Health
 *
 * Return a simple health status payload.
 */
export const getHealth = <ThrowOnError extends boolean = false>(
  options?: Options<GetHealthData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetHealthResponses, unknown, ThrowOnError>({
    url: "/health",
    ...options,
  });

/**
 * Auth Config
 *
 * Which sign-in flow this deployment uses — SSO redirect or password form.
 *
 * Unauthenticated by design: the SPA has to call it before anyone is signed in
 * to know which login screen to render.
 */
export const getAuthConfig = <ThrowOnError extends boolean = false>(
  options?: Options<GetAuthConfigData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetAuthConfigResponses, unknown, ThrowOnError>({
    url: "/auth/config",
    ...options,
  });

/**
 * Auth Login
 *
 * Sign in with email + password, verified against Supabase Auth.
 *
 * On success runs the same post-authentication pipeline as the SSO callback
 * (domain allowlist, JIT provisioning, sign-in + activity logging) and sets the
 * same HMAC session cookie. Only the credential check differs between providers.
 */
export const loginWithPassword = <ThrowOnError extends boolean = false>(
  options: Options<LoginWithPasswordData, ThrowOnError>,
) =>
  (options.client ?? client).post<
    LoginWithPasswordResponses,
    LoginWithPasswordErrors,
    ThrowOnError
  >({
    url: "/auth/login",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Sso Login
 *
 * Begin Entra ID SSO — redirect the browser to Microsoft to sign in.
 *
 * Hands off to Entra's authorize endpoint (Authorization Code + PKCE). The
 * browser returns to `/auth/sso/callback` with an authorization code. 503 when
 * Entra is unconfigured, 404 when this deployment authenticates with passwords.
 */
export const ssoLogin = <ThrowOnError extends boolean = false>(
  options?: Options<SsoLoginData, ThrowOnError>,
) =>
  (options?.client ?? client).get<unknown, unknown, ThrowOnError>({
    url: "/auth/sso/login",
    ...options,
  });

/**
 * Auth Sso Callback
 *
 * Complete Entra ID SSO and start an app session.
 *
 * Exchanges the authorization code for an ID token, enforces the email-domain
 * allowlist, JIT-provisions (or matches) the user, mints the HMAC session
 * cookie, and redirects into the SPA. Any failure redirects to
 * `{FRONTEND_URL}/login?error=<code>`. 404 when this deployment authenticates
 * with passwords — the whole SSO surface is gated by AUTH_PROVIDER, not just
 * the entry point.
 */
export const ssoCallback = <ThrowOnError extends boolean = false>(
  options?: Options<SsoCallbackData, ThrowOnError>,
) =>
  (options?.client ?? client).get<unknown, unknown, ThrowOnError>({
    url: "/auth/sso/callback",
    ...options,
  });

/**
 * Auth Logout
 *
 * Sign out — clear the session cookie.
 */
export const logout = <ThrowOnError extends boolean = false>(
  options?: Options<LogoutData, ThrowOnError>,
) =>
  (options?.client ?? client).post<LogoutResponses, unknown, ThrowOnError>({
    url: "/auth/logout",
    ...options,
  });

/**
 * Auth Me
 *
 * Get the current user's profile.
 */
export const getMe = <ThrowOnError extends boolean = false>(
  options?: Options<GetMeData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetMeResponses, unknown, ThrowOnError>({
    url: "/auth/me",
    ...options,
  });

/**
 * Auth Me Detail
 *
 * Get the current user's profile with timeline fields.
 */
export const getMeDetail = <ThrowOnError extends boolean = false>(
  options?: Options<GetMeDetailData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetMeDetailResponses, unknown, ThrowOnError>({
    url: "/auth/me/detail",
    ...options,
  });

/**
 * Auth Update Profile
 *
 * Update current user's name.
 */
export const updateProfile = <ThrowOnError extends boolean = false>(
  options: Options<UpdateProfileData, ThrowOnError>,
) =>
  (options.client ?? client).patch<UpdateProfileResponses, UpdateProfileErrors, ThrowOnError>({
    url: "/auth/profile",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Activity
 *
 * Get the current user's activity log.
 */
export const getActivity = <ThrowOnError extends boolean = false>(
  options?: Options<GetActivityData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetActivityResponses, unknown, ThrowOnError>({
    url: "/auth/activity",
    ...options,
  });

/**
 * Admin List Users
 *
 * List all users (admin only).
 */
export const adminListUsers = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListUsersData, ThrowOnError>,
) =>
  (options?.client ?? client).get<AdminListUsersResponses, unknown, ThrowOnError>({
    url: "/admin/users",
    ...options,
  });

/**
 * Admin Delete User
 *
 * Delete a user (admin only). Self-deletion blocked.
 */
export const adminDeleteUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminDeleteUserData, ThrowOnError>,
) =>
  (options.client ?? client).delete<AdminDeleteUserResponses, AdminDeleteUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
  });

/**
 * Admin Get User
 *
 * Get a single user detail with activity logs (admin only).
 */
export const adminGetUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminGetUserData, ThrowOnError>,
) =>
  (options.client ?? client).get<AdminGetUserResponses, AdminGetUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
  });

/**
 * Admin Update User
 *
 * Update a user's name or role (admin only). Self-demotion blocked.
 */
export const adminUpdateUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminUpdateUserData, ThrowOnError>,
) =>
  (options.client ?? client).put<AdminUpdateUserResponses, AdminUpdateUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Admin List Activity
 *
 * List activity across all users (admin only).
 *
 * Search, filtering, sorting, and pagination are all applied server-side by
 * the data layer — the client sends params and renders results.
 */
export const adminListActivity = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListActivityData, ThrowOnError>,
) =>
  (options?.client ?? client).get<
    AdminListActivityResponses,
    AdminListActivityErrors,
    ThrowOnError
  >({ url: "/admin/activity", ...options });

/**
 * Admin Activity Analytics
 *
 * Aggregated activity analytics for the admin dashboard (admin only).
 */
export const adminActivityAnalytics = <ThrowOnError extends boolean = false>(
  options?: Options<AdminActivityAnalyticsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<
    AdminActivityAnalyticsResponses,
    AdminActivityAnalyticsErrors,
    ThrowOnError
  >({ url: "/admin/activity/analytics", ...options });

/**
 * Admin Activity Actions
 *
 * Distinct activity actions across all time (admin only).
 *
 * Used to populate the activity filter dropdown so it matches the full,
 * unbounded table scope rather than the windowed analytics breakdown.
 */
export const adminActivityActions = <ThrowOnError extends boolean = false>(
  options?: Options<AdminActivityActionsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<AdminActivityActionsResponses, unknown, ThrowOnError>({
    url: "/admin/activity/actions",
    ...options,
  });
